/**
 * Status Update Components
 * 
 * Reusable components for displaying and updating appointment statuses
 * with the professional status management system.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

import { 
  AppointmentStatus, 
  APPOINTMENT_STATUS_CONFIG, 
  AppointmentStatusManager 
} from '../../types/appointment-status';
import { useAppointmentStatusUpdate, useAppointmentStatusBatch } from '../../services/appointment-status-service';

interface StatusBadgeProps {
  status: AppointmentStatus;
  compact?: boolean;
  showIcon?: boolean;
  className?: string;
}

/**
 * Status Badge Component
 * Displays status with consistent styling
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  compact = false, 
  showIcon = true,
  className = '' 
}) => {
  const displayInfo = AppointmentStatusManager.getDisplayInfo(status, compact);

  const getStatusIcon = () => {
    switch (status) {
      case 'pending': return <ClockIcon className="h-3 w-3" />;
      case 'confirmed': return <CheckCircleIcon className="h-3 w-3" />;
      case 'checked-in': return <UserIcon className="h-3 w-3" />;
      case 'in-progress': return <PlayIcon className="h-3 w-3" />;
      case 'completed': return <CheckCircleIcon className="h-3 w-3" />;
      case 'cancelled': return <XCircleIcon className="h-3 w-3" />;
      case 'no-show': return <ExclamationTriangleIcon className="h-3 w-3" />;
      case 'rescheduled': return <CalendarIcon className="h-3 w-3" />;
      default: return <ClockIcon className="h-3 w-3" />;
    }
  };

  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${className}`}
      style={{
        backgroundColor: displayInfo.bgColor,
        color: displayInfo.color,
        borderColor: displayInfo.borderColor
      }}
      title={displayInfo.description}
    >
      {showIcon && getStatusIcon()}
      <span>{displayInfo.label}</span>
    </span>
  );
};

interface StatusTransitionButtonProps {
  currentStatus: AppointmentStatus;
  targetStatus: AppointmentStatus;
  appointmentId: string;
  onStatusChanged?: (appointmentId: string, newStatus: AppointmentStatus) => void;
  onError?: (error: string) => void;
  className?: string;
}

/**
 * Status Transition Button
 * Button for changing status with validation
 */
export const StatusTransitionButton: React.FC<StatusTransitionButtonProps> = ({
  currentStatus,
  targetStatus,
  appointmentId,
  onStatusChanged,
  onError,
  className = ''
}) => {
  const { t } = useTranslation(['appointments']);
  const { updateStatus, loading } = useAppointmentStatusUpdate();
  const targetConfig = APPOINTMENT_STATUS_CONFIG[targetStatus];
  const canTransition = AppointmentStatusManager.canTransition(currentStatus, targetStatus);

  if (!canTransition) return null;

  const handleClick = async () => {
    try {
      const needsConfirmation = targetConfig.requiresConfirmation;
      
      if (needsConfirmation) {
        const confirmed = confirm(
          t('statusComponents.confirmStatusChange', { status: targetConfig.label.toLowerCase() })
        );
        if (!confirmed) return;
      }

      const result = await updateStatus({
        appointmentId,
        newStatus: targetStatus
      });

      if (result.success && onStatusChanged) {
        onStatusChanged(appointmentId, targetStatus);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('statusComponents.failedToUpdateStatus');
      console.error('Status update failed:', error);
      if (onError) {
        onError(errorMessage);
      } else {
        alert(`${t('statusComponents.failedToUpdateStatus')}: ${errorMessage}`);
      }
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`px-2 py-1 text-xs rounded border hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{
        backgroundColor: targetConfig.bgColor,
        color: targetConfig.color,
        borderColor: targetConfig.borderColor
      }}
    >
      {loading ? t('statusComponents.updating') : t('statusComponents.markAs', { status: targetConfig.label })}
    </button>
  );
};

interface StatusUpdateControlsProps {
  currentStatus: AppointmentStatus;
  appointmentId: string;
  appointmentTime?: Date;
  onStatusChanged?: (appointmentId: string, newStatus: AppointmentStatus) => void;
  onError?: (error: string) => void;
  layout?: 'horizontal' | 'vertical' | 'grid';
  showCurrentStatus?: boolean;
  className?: string;
}

/**
 * Complete Status Update Controls
 * Shows all available status transitions
 */
export const StatusUpdateControls: React.FC<StatusUpdateControlsProps> = ({
  currentStatus,
  appointmentId,
  appointmentTime,
  onStatusChanged,
  onError,
  layout = 'horizontal',
  showCurrentStatus = true,
  className = ''
}) => {
  const { t } = useTranslation(['appointments']);
  const availableTransitions = AppointmentStatusManager.getValidTransitions(currentStatus);
  const isOverdue = appointmentTime && new Date() > appointmentTime;
  
  if (availableTransitions.length === 0) {
    return showCurrentStatus ? (
      <div className={className}>
        <StatusBadge status={currentStatus} />
        <span className="text-xs text-muted-foreground ml-2">
          ({t('statusComponents.finalStatus')})
        </span>
      </div>
    ) : null;
  }

  const layoutClasses = {
    horizontal: 'flex flex-wrap gap-1',
    vertical: 'flex flex-col gap-1',
    grid: 'grid grid-cols-2 gap-1'
  };

  return (
    <div className={className}>
      {showCurrentStatus && (
        <div className="flex items-center gap-2 mb-2">
          <StatusBadge status={currentStatus} />
          {isOverdue && currentStatus === 'confirmed' && (
            <span className="text-xs text-red-500 font-medium">
              ({t('statusComponents.overdueLabel')})
            </span>
          )}
        </div>
      )}
      
      <div className={layoutClasses[layout]}>
        {availableTransitions.map((targetStatus: AppointmentStatus) => (
          <StatusTransitionButton
            key={targetStatus}
            currentStatus={currentStatus}
            targetStatus={targetStatus}
            appointmentId={appointmentId}
            onStatusChanged={onStatusChanged}
            onError={onError}
          />
        ))}
      </div>
    </div>
  );
};

interface StatusTimelineProps {
  appointmentId: string;
  className?: string;
}

/**
 * Status Timeline Component
 * Shows the history of status changes
 */
export const StatusTimeline: React.FC<StatusTimelineProps> = ({ 
  className = '' 
}) => {
  const { t } = useTranslation(['appointments']);
  // This would use the useAppointmentStatusHistory hook
  // For now, showing the structure
  
  return (
    <div className={`bg-card rounded-lg border border-border p-4 ${className}`}>
      <h4 className="text-sm font-medium text-card-foreground mb-3">{t('statusComponents.statusHistory')}</h4>
      <div className="space-y-2 text-xs text-muted-foreground">
        <div className="flex justify-between">
          <span>{t('statusComponents.createdAsPending')}</span>
          <span>{t('statusComponents.hoursAgo', { count: 2 })}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('statusComponents.confirmedByPatient')}</span>
          <span>{t('statusComponents.hoursAgo', { count: 1 })}</span>
        </div>
        <div className="text-center text-muted-foreground py-2">
          <span>--- {t('statusComponents.currentStatus')} ---</span>
        </div>
      </div>
    </div>
  );
};

interface BulkStatusUpdateProps {
  selectedAppointments: { id: string; currentStatus: AppointmentStatus }[];
  onBulkUpdateComplete?: (results: any) => void;
  onError?: (error: string) => void;
}

/**
 * Bulk Status Update Component
 * For updating multiple appointments at once
 */
export const BulkStatusUpdate: React.FC<BulkStatusUpdateProps> = ({
  selectedAppointments,
  onBulkUpdateComplete,
  onError
}) => {
  const { t } = useTranslation(['appointments']);
  const [selectedNewStatus, setSelectedNewStatus] = useState<AppointmentStatus | ''>('');
  const { updateStatusBatch, loading: isLoading } = useAppointmentStatusBatch();
  
  // Find common available transitions for all selected appointments
  const commonTransitions = selectedAppointments.length > 0 
    ? AppointmentStatusManager.getValidTransitions(selectedAppointments[0].currentStatus)
        .filter((status: AppointmentStatus) => 
          selectedAppointments.every(apt => 
            AppointmentStatusManager.canTransition(apt.currentStatus, status)
          )
        )
    : [];

  const handleBulkUpdate = async () => {
    if (!selectedNewStatus || selectedAppointments.length === 0) return;

    const needsConfirmation = APPOINTMENT_STATUS_CONFIG[selectedNewStatus].requiresConfirmation;
    
    if (needsConfirmation) {
      const confirmed = confirm(
        t('statusComponents.confirmBulkUpdate', { count: selectedAppointments.length, status: selectedNewStatus })
      );
      if (!confirmed) return;
    }

    try {
      const result = await updateStatusBatch({
        updates: selectedAppointments.map(apt => ({
          appointmentId: apt.id,
          newStatus: selectedNewStatus as AppointmentStatus
        }))
      });

      if (onBulkUpdateComplete) {
        onBulkUpdateComplete(result);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('statusComponents.bulkUpdateFailed');
      if (onError) {
        onError(errorMessage);
      } else {
        alert(`${t('statusComponents.bulkUpdateFailed')}: ${errorMessage}`);
      }
    }
  };

  if (selectedAppointments.length === 0) return null;

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <h4 className="text-sm font-medium text-card-foreground mb-3">
        {t('statusComponents.bulkUpdate')} ({t('statusComponents.appointmentsCount', { count: selectedAppointments.length })})
      </h4>
      
      <div className="flex items-center gap-3">
        <select
          value={selectedNewStatus}
          onChange={(e) => setSelectedNewStatus(e.target.value as AppointmentStatus)}
          className="bg-background border border-border rounded px-3 py-1 text-sm"
        >
          <option value="">{t('statusComponents.selectNewStatus')}</option>
          {commonTransitions.map((status: AppointmentStatus) => {
            const config = APPOINTMENT_STATUS_CONFIG[status];
            return (
              <option key={status} value={status}>
                {config.label}
              </option>
            );
          })}
        </select>
        
        <button
          onClick={handleBulkUpdate}
          disabled={!selectedNewStatus || isLoading}
          className="px-3 py-1 bg-primary text-primary-foreground text-sm rounded hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? t('statusComponents.updating') : t('statusComponents.updateAll')}
        </button>
      </div>
      
      {commonTransitions.length === 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {t('statusComponents.noCommonTransitions')}
        </p>
      )}
    </div>
  );
};

/**
 * Status Filter Component
 * For filtering appointments by status
 */
interface StatusFilterProps {
  selectedStatuses: AppointmentStatus[];
  onStatusesChange: (statuses: AppointmentStatus[]) => void;
  showCounts?: boolean;
  statusCounts?: Record<AppointmentStatus, number>;
  className?: string;
}

export const StatusFilter: React.FC<StatusFilterProps> = ({
  selectedStatuses,
  onStatusesChange,
  showCounts = false,
  statusCounts = {} as Record<AppointmentStatus, number>,
  className = ''
}) => {
  const { t } = useTranslation(['appointments']);
  const allStatuses = Object.keys(APPOINTMENT_STATUS_CONFIG) as AppointmentStatus[];
  const sortedStatuses = AppointmentStatusManager.sortByPriority(allStatuses);

  const toggleStatus = (status: AppointmentStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusesChange(selectedStatuses.filter(s => s !== status));
    } else {
      onStatusesChange([...selectedStatuses, status]);
    }
  };

  const selectAll = () => {
    onStatusesChange(allStatuses);
  };

  const selectNone = () => {
    onStatusesChange([]);
  };

  return (
    <div className={`bg-card rounded-lg border border-border p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-card-foreground">{t('statusComponents.filterByStatus')}</h4>
        <div className="flex gap-2">
          <button 
            onClick={selectAll}
            className="text-xs text-primary hover:underline"
          >
            {t('statusComponents.all')}
          </button>
          <button 
            onClick={selectNone}
            className="text-xs text-muted-foreground hover:underline"
          >
            {t('statusComponents.none')}
          </button>
        </div>
      </div>
      
      <div className="space-y-2">
        {sortedStatuses.map((status: AppointmentStatus) => {
          const isSelected = selectedStatuses.includes(status);
          const count = statusCounts[status] || 0;
          
          return (
            <label 
              key={status}
              className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 px-2 py-1 rounded"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleStatus(status)}
                className="rounded border-border"
              />
              <StatusBadge status={status} compact />
              {showCounts && (
                <span className="text-xs text-muted-foreground ml-auto">
                  ({count})
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};